import type { PersistedTab, SecondaryPostEventPersistedTab } from 'apiConnector/tabs/tabs.model';
import { isSecondaryPostEventPersistedTab } from 'apiConnector/tabs/tabs.model';
import { secondaryMarketAmendCpOrderFetchRequested } from 'state/secondaryMarket/cpOrder/cpOrderForm/original/cpOrderOriginalForm.actions';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import { secondaryMarketOriginalFormActionsFactory } from 'state/secondaryMarket/shared/form/original/original.actions';
import type { Thunk } from 'state/state.common';
import { SecondaryMarketPostEventTabType, TabsForWorkspace } from 'state/workspaces/model';
import { workspaceTabActivated, workspaceTabCreated, workspaceTabsFetched } from 'state/workspaces/workspace.actions';

import { secondaryMarketPostEventWorkspaceAddTab } from './secondaryMarketPostEvent.actions';
import { findSecondaryPostEventTabByOrderId } from './secondaryMarketPostEvent.selector';

export const secondaryMarketPostEventLiveThunks = secondaryMarketPostEventThunksFactory('live', 'LIVE');
export const secondaryMarketPostEventLimitOrderThunks = secondaryMarketPostEventThunksFactory(
  'limitOrder',
  'LIMIT_ORDER',
);
export function secondaryMarketPostEventThunksFactory(
  tabType: Exclude<TabsForWorkspace['secondaryMarketPostEvents'], 'closePriceOrder'>,
  orderActionType: Exclude<OrderActionType, 'CP_ORDER'>,
) {
  const originalFormActions = secondaryMarketOriginalFormActionsFactory(orderActionType);
  const openAmendTabThunk =
    (orderId: string, tabName: string): Thunk<void> =>
    (dispatch, getState) => {
      const state = getState();
      const existingTabId = findSecondaryPostEventTabByOrderId(state, orderId);
      if (existingTabId === undefined) {
        dispatch(secondaryModifyTabCreatedThunk(orderId, tabName));
      } else {
        dispatch(workspaceTabActivated(existingTabId, 'secondaryMarketPostEvents', tabType));
      }
    };
  const secondaryModifyTabCreatedThunk = (orderId: string, tabName: string): Thunk<void> => {
    return (dispatch, _getState, { getNewGuid }) => {
      const tabId = getNewGuid();
      dispatch(secondaryMarketPostEventWorkspaceAddTab(tabId, orderId, tabType));
      dispatch(workspaceTabCreated(tabId, 'secondaryMarketPostEvents', tabType, tabName));
      dispatch(originalFormActions.secondaryMarketAmendFetchRequested(tabId, orderId));
    };
  };
  return { openAmendTabThunk, secondaryModifyTabCreatedThunk };
}

export function openAmendCpOrderTabThunk(orderId: string, tabName: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const existingTabId = findSecondaryPostEventTabByOrderId(state, orderId);
    if (existingTabId === undefined) {
      dispatch(secondaryModifyCpOrderTabCreatedThunk(orderId, tabName));
    } else {
      dispatch(workspaceTabActivated(existingTabId, 'secondaryMarketPostEvents', 'closePriceOrder'));
    }
  };
}

export function secondaryModifyCpOrderTabCreatedThunk(orderId: string, tabName: string): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    const tabId = getNewGuid();

    dispatch(secondaryMarketPostEventWorkspaceAddTab(tabId, orderId, 'closePriceOrder'));
    dispatch(workspaceTabCreated(tabId, 'secondaryMarketPostEvents', 'closePriceOrder', tabName));
    dispatch(secondaryMarketAmendCpOrderFetchRequested(tabId, orderId));
  };
}

export function secondaryPostEventsWorkspaceTabsFetchedThunk(
  tabs: PersistedTab[],
  activeTabId: string | undefined,
  tabFetchRequestId?: string,
): Thunk<void> {
  return (dispatch, _getState, __) => {
    const postEventsTabs: SecondaryPostEventPersistedTab[] = tabs.map(tab => {
      if (!isSecondaryPostEventPersistedTab(tab)) {
        throw new Error(`inconsistent tab type: type=${tab.type}/${tab.tabContentType}`);
      }
      return tab;
    });

    dispatch(workspaceTabsFetched(postEventsTabs, activeTabId, 'secondaryMarketPostEvents', tabFetchRequestId));
    postEventsTabs.forEach(({ quoteId: orderId, id: tabId, type }) => {
      if (type !== undefined) {
        dispatch(secondaryMarketPostEventWorkspaceAddTab(tabId, orderId, type as SecondaryMarketPostEventTabType));
      }
    });
  };
}
